import { emitApiTelemetry, emitNetworkError, perfNow, sanitisePath } from "../../../telemetry/apiTelemetry";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import { reportGraphOutcome } from "../../../shared/api/m365Health";

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

export class GraphApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "GraphApiError";
    this.status = status;
  }
}

/**
 * Issue one Graph call with a one-shot 401→refresh→retry and the surrounding
 * telemetry. `send` receives the bearer to use, so every verb and
 * every body shape share exactly one copy of the retry+telemetry block.
 *
 * Microsoft Graph is a third-party service reached directly from the add-in, so
 * it carries the "integration" backend tag (like serviceFetch). `templatedPath`
 * is the already-sanitised path (origin + ids stripped) — telemetry never sees
 * a raw URL.
 *
 * Returns the raw `Response` so the caller decides how to treat a non-2xx
 * (throw vs. skip). Re-throws only on a network/fetch throw, after emitting
 * `error.network`.
 */
async function graphSend(
  send: (bearer: string) => Promise<Response>,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  templatedPath: string,
  token: string,
  refresh?: RefreshTokenFn,
): Promise<Response> {
  const started = perfNow();

  let response: Response;
  try {
    response = await send(token);
  } catch (err) {
    emitNetworkError(templatedPath, method, started, "integration", err);
    reportGraphOutcome(null);
    throw err;
  }

  // Expired Graph token → re-acquire once and retry, so the call recovers
  // silently instead of surfacing a 401 to the module. Only when a `refresh`
  // callback is provided and yields a fresh token.
  if (response.status === 401 && refresh) {
    const fresh = await refresh();
    if (fresh) {
      try {
        response = await send(fresh);
      } catch (err) {
        emitNetworkError(templatedPath, method, started, "integration", err);
        reportGraphOutcome(null);
        throw err;
      }
    }
  }

  emitApiTelemetry(templatedPath, method, started, response.status, "integration");
  reportGraphOutcome(response.status);
  return response;
}

async function graphFetch<T>(
  endpoint: string,
  token: string,
  refresh?: RefreshTokenFn,
): Promise<T> {
  const url = `${GRAPH_BASE}${endpoint}`;
  const response = await graphSend(
    (bearer) => fetch(url, { headers: { Authorization: `Bearer ${bearer}` } }),
    "GET",
    sanitisePath(url),
    token,
    refresh,
  );

  if (!response.ok) {
    throw new GraphApiError(response.status, `Graph API request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

/**
 * GET a Graph path, or an absolute Graph URL such as an `@odata.nextLink`, with
 * extra request headers (e.g. `Prefer`). Absolute URLs must be on the Graph
 * origin so the bearer is never sent to another host.
 */
export async function graphGetJson<T>(
  pathOrUrl: string,
  token: string,
  refresh?: RefreshTokenFn,
  headers: Readonly<Record<string, string>> = {},
): Promise<T> {
  const url = pathOrUrl.startsWith("/") ? `${GRAPH_BASE}${pathOrUrl}` : pathOrUrl;
  if (!url.startsWith(`${GRAPH_BASE}/`)) {
    throw new GraphApiError(0, "Refusing to send a Graph token to a non-Graph URL");
  }
  const response = await graphSend(
    (bearer) => fetch(url, { headers: { ...headers, Authorization: `Bearer ${bearer}` } }),
    "GET",
    sanitisePath(url),
    token,
    refresh,
  );

  if (!response.ok) {
    throw new GraphApiError(response.status, `Graph API request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

/**
 * GET a Graph path as raw bytes (e.g. an attachment's `/$value`). The response
 * is kept as a Blob, never decoded as text, so binary files aren't corrupted.
 */
export async function graphGetBlob(path: string, token: string, refresh?: RefreshTokenFn): Promise<Blob> {
  const url = `${GRAPH_BASE}${path}`;
  const response = await graphSend(
    (bearer) => fetch(url, { headers: { Authorization: `Bearer ${bearer}` } }),
    "GET",
    sanitisePath(url),
    token,
    refresh,
  );
  if (!response.ok) {
    throw new GraphApiError(response.status, `Graph API request failed: ${response.status}`);
  }
  return response.blob();
}

/**
 * Send a write (POST / PATCH / DELETE) with an optional JSON body to a Graph
 * path. Returns the raw Response because several calendar actions (accept,
 * decline, cancel) answer 202 with no body. Throws GraphApiError on non-2xx.
 */
export async function graphWrite(
  method: "POST" | "PATCH" | "DELETE",
  path: string,
  body: unknown,
  token: string,
  refresh?: RefreshTokenFn,
): Promise<Response> {
  const url = `${GRAPH_BASE}${path}`;
  const response = await graphSend(
    (bearer) =>
      fetch(url, {
        method,
        headers: { Authorization: `Bearer ${bearer}`, "Content-Type": "application/json" },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      }),
    method,
    sanitisePath(url),
    token,
    refresh,
  );
  if (!response.ok) {
    throw new GraphApiError(response.status, `Graph API request failed: ${response.status}`);
  }
  return response;
}

/** POST a JSON body to a Graph path (e.g. "/me/calendar/getSchedule") and parse the JSON reply. */
export async function graphPostJson<T>(
  path: string,
  body: unknown,
  token: string,
  refresh?: RefreshTokenFn,
): Promise<T> {
  const response = await graphPost(`${GRAPH_BASE}${path}`, JSON.stringify(body), token, refresh);
  if (!response.ok) {
    throw new GraphApiError(response.status, `Graph API request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

/**
 * POST a JSON body to a Graph endpoint. Shared by the `$batch`, `/search/query`,
 * and `/preview` POST callers; see {@link graphSend} for the retry/telemetry
 * contract and the non-2xx handling left to the caller.
 */
function graphPost(
  url: string,
  body: BodyInit,
  token: string,
  refresh?: RefreshTokenFn,
): Promise<Response> {
  return graphSend(
    (bearer) =>
      fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${bearer}`, "Content-Type": "application/json" },
        body,
      }),
    "POST",
    sanitisePath(url),
    token,
    refresh,
  );
}

interface GraphList<T> {
  value: T[];
}

export interface GraphEmail {
  id: string;
  subject: string;
  from: { emailAddress: { name: string; address: string } };
  receivedDateTime: string;
  bodyPreview: string;
  importance: string;
  isRead: boolean;
  hasAttachments: boolean;
}

export interface GraphCalendarEvent {
  id: string;
  subject: string;
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  organizer: { emailAddress: { name: string; address: string } };
  isAllDay: boolean;
  location: { displayName: string };
}

export interface GraphUserProfile {
  displayName: string;
  mail: string;
  userPrincipalName: string;
  jobTitle: string | null;
}

export interface GraphDriveItem {
  id: string;
  name: string;
  size: number;
  webUrl: string;
  lastModifiedDateTime: string;
  file?: { mimeType: string };
  folder?: { childCount: number };
  /** Tag values from the document's SharePoint metadata columns (see extractTags). */
  tags?: string[];
}

/**
 * SharePoint list-item `fields` keys that are built-in / plumbing, not user tags.
 * Everything else with a plain string value is treated as a document "tag" — so
 * no specific column name is hardcoded; any choice/text column (Document Type,
 * Category, Status, …) surfaces automatically.
 */
const NON_TAG_FIELDS = new Set([
  "id", "ID", "Title", "ContentType", "Created", "Modified", "Author", "Editor",
  "FileLeafRef", "FileRef", "FileDirRef", "FileSizeDisplay", "File_x0020_Type",
  "LinkFilename", "LinkFilenameNoMenu", "LinkFilename2", "DocIcon", "ItemChildCount",
  "FolderChildCount", "ParentLeafName", "ParentVersionString", "ParentUniqueId",
  "ServerRedirectedEmbedUri", "ServerRedirectedEmbedUrl", "ComplianceAssetId",
  "AppAuthor", "AppEditor", "SelectFilename", "Edit", "PermMask", "GUID", "Order",
  "owshiddenversion", "Attachments", "CheckoutUser", "IsCheckedoutToLocal",
  "MediaServiceImageTags", "SMTotalSize", "SMLastModifiedDate", "StreamHash",
  "SMTotalFileStreamSize", "SMTotalFileCount", "Combine", "RepairDocument",
]);

/**
 * Extract a document's tags from its SharePoint list-item `fields` bag. Returns
 * the label values of user metadata columns — nothing is hardcoded to a named
 * column. Handles the shapes SharePoint returns: plain text/choice columns come
 * back as strings; managed-metadata (taxonomy) columns come back as an object
 * `{ Label, TermGuid }` (single value) or an array of them (multi-value, e.g. a
 * "Document Type" term column or Enterprise Keywords) — we take the `.Label`.
 * Skips internal/system fields (see NON_TAG_FIELDS), OData annotations,
 * `_`-prefixed / `*LookupId` keys, the hidden `*_0` taxonomy note fields, and
 * `TaxCatchAll*` so a term isn't surfaced twice. Values that aren't strings or
 * `{ Label }` objects (people/lookups/dates) are ignored. Deduped, order-stable.
 */
function extractTags(fields: Record<string, unknown> | undefined): string[] {
  if (!fields) return [];
  const tags: string[] = [];
  const push = (value: unknown) => {
    if (typeof value === "string") {
      if (value.trim()) tags.push(value.trim());
      return;
    }
    if (value && typeof value === "object") {
      const label = (value as { Label?: unknown }).Label;
      if (typeof label === "string" && label.trim()) tags.push(label.trim());
    }
  };
  for (const [key, value] of Object.entries(fields)) {
    if (key.startsWith("_") || key.startsWith("@") || key.startsWith("OData__")) continue;
    if (key.endsWith("LookupId") || key.endsWith("_0")) continue;
    if (key === "TaxCatchAll" || key === "TaxCatchAllLabel") continue;
    if (NON_TAG_FIELDS.has(key)) continue;
    if (Array.isArray(value)) value.forEach(push);
    else push(value);
  }
  return [...new Set(tags)];
}

export interface GraphSite {
  id: string;
  name?: string;
  displayName?: string;
  description?: string;
  webUrl: string;
  lastModifiedDateTime?: string;
}

/**
 * A driveItem hit from the Microsoft Search API, normalised to what the
 * Knowledge Base results list needs. `driveId` is the parent drive (used to
 * open the file read-only via `fetchItemPreviewUrl` with `/drives/{driveId}`);
 * `sourceLabel` is a short "which library/folder" line for the meta row.
 */
export interface GraphSearchItem {
  id: string;
  name: string;
  webUrl: string;
  size: number;
  lastModifiedDateTime: string;
  driveId: string | null;
  sourceLabel: string;
  /** True when the hit is a folder (opens its SharePoint URL, not a preview). */
  isFolder: boolean;
  /** Tag values from the item's SharePoint metadata columns (see extractTags). */
  tags?: string[];
}

export async function fetchEmails(
  token: string,
  top = 25,
  refresh?: RefreshTokenFn,
): Promise<GraphEmail[]> {
  const result = await graphFetch<GraphList<GraphEmail>>(
    `/me/mailFolders/inbox/messages?$top=${top}&$orderby=receivedDateTime desc`,
    token,
    refresh,
  );
  return result.value;
}

/**
 * Inbox item counts for the boot greeting (spec ST-02).
 *
 * Reads the folder resource rather than counting a message page: Graph already
 * maintains `unreadItemCount` on the folder, so this is one small request with
 * an exact number. Counting `fetchEmails` results instead would cap the answer
 * at the page size and report "25 emails" for a mailbox holding hundreds.
 */
export async function fetchInboxCounts(
  token: string,
  refresh?: RefreshTokenFn,
): Promise<{ unread: number; total: number }> {
  const folder = await graphFetch<{ unreadItemCount?: number; totalItemCount?: number }>(
    "/me/mailFolders/inbox?$select=unreadItemCount,totalItemCount",
    token,
    refresh,
  );
  return { unread: folder.unreadItemCount ?? 0, total: folder.totalItemCount ?? 0 };
}

export async function fetchCalendarEvents(
  token: string,
  startDate: string,
  endDate: string,
  refresh?: RefreshTokenFn,
): Promise<GraphCalendarEvent[]> {
  const result = await graphFetch<GraphList<GraphCalendarEvent>>(
    `/me/calendarView?startDateTime=${startDate}&endDateTime=${endDate}&$orderby=start/dateTime`,
    token,
    refresh,
  );
  return result.value;
}

export async function getUserProfile(
  token: string,
  refresh?: RefreshTokenFn,
): Promise<GraphUserProfile> {
  return graphFetch<GraphUserProfile>("/me?$select=displayName,mail,userPrincipalName,jobTitle", token, refresh);
}

export interface CalendarEventDraft {
  subject: string;
  /** Invite body, already composed as HTML by the backend submit tool. */
  bodyHtml: string;
  start: Date;
  end: Date;
  location: string;
  attendees: string[];
}

export interface GraphCreatedEvent {
  id: string;
  /** OWA deep link to the created event — the last-resort way to open it. */
  webLink: string;
}

/** Local wall clock as Graph's ``dateTime`` (no offset — it pairs with ``timeZone``). */
function toGraphLocalDateTime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/**
 * Create a calendar event via `POST /me/events`.
 *
 * The CM-01 fallback for hosts with no `displayNewAppointmentFormAsync` (New
 * Outlook, OWA, some Mac builds): specification §6.2 — "a fallback for hosts
 * without that API creates the event via Graph after an external-recipient
 * confirmation, then opens it in the appointment form."
 *
 * Times are sent as the user's local wall clock plus the host's IANA zone
 * rather than as UTC instants. The backend submit tools produce naive local ISO
 * strings, so re-stamping them in the same frame is what keeps a 14:00 booking
 * at 14:00 for the organiser.
 *
 * NOTE: Graph mails the invitation to `attendees` as soon as this returns — the
 * external-recipient guard in the caller is what the user consents through.
 */
export async function createCalendarEvent(
  draft: CalendarEventDraft,
  token: string,
  refresh?: RefreshTokenFn,
): Promise<GraphCreatedEvent> {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const body = {
    subject: draft.subject,
    body: { contentType: "HTML", content: draft.bodyHtml },
    start: { dateTime: toGraphLocalDateTime(draft.start), timeZone },
    end: { dateTime: toGraphLocalDateTime(draft.end), timeZone },
    ...(draft.location ? { location: { displayName: draft.location } } : {}),
    attendees: draft.attendees.map((address) => ({
      emailAddress: { address },
      type: "required",
    })),
  };
  const response = await graphPost(`${GRAPH_BASE}/me/events`, JSON.stringify(body), token, refresh);
  if (!response.ok) {
    throw new GraphApiError(response.status, `Graph event creation failed: ${response.status}`);
  }
  const created = (await response.json()) as { id?: string; webLink?: string };
  return { id: created.id ?? "", webLink: created.webLink ?? "" };
}

/**
 * Fetch the children of a folder under any drive base path.
 *
 * `drivePath` is the Graph segment that identifies the drive — e.g.
 * `/me/drive` for the user's OneDrive or `/sites/{siteId}/drive` for the
 * default document library of a SharePoint site. `parentId` is `null` to
 * read the drive root, or an item id to read a sub-folder.
 */
export async function fetchDriveContents(
  token: string,
  drivePath: string,
  parentId: string | null,
  refresh?: RefreshTokenFn,
): Promise<GraphDriveItem[]> {
  const segment = parentId ? `/items/${parentId}/children` : "/root/children";
  const result = await graphFetch<GraphList<GraphDriveItem & { listItem?: { fields?: Record<string, unknown> } }>>(
    `${drivePath}${segment}?$top=200&$orderby=name&$select=id,name,size,webUrl,lastModifiedDateTime,file,folder&$expand=listItem($expand=fields)`,
    token,
    refresh,
  );
  return result.value.map((it) => {
    const base: GraphDriveItem = {
      id: it.id,
      name: it.name,
      size: it.size,
      webUrl: it.webUrl,
      lastModifiedDateTime: it.lastModifiedDateTime,
      file: it.file,
      folder: it.folder,
    };
    // Only attach `tags` when the item actually has any — an empty array would
    // otherwise pollute every item (and break equality-based callers/tests).
    // Mirrors searchDriveItems, which likewise omits tags when there are none.
    const tags = extractTags(it.listItem?.fields);
    return tags.length > 0 ? { ...base, tags } : base;
  });
}

/**
 * List the children of a folder addressed by its PATH under a drive (as opposed
 * to `fetchDriveContents`, which addresses the parent by item id). `folderPath`
 * is relative to the drive root, e.g. `AiVA/Engage/12345`.
 *
 * Returns `[]` when the folder does not exist (404) so callers can treat "no
 * folder yet" and "empty folder" identically — used to surface a job's staged
 * attachments in the request-detail view without a server round-trip, since the
 * add-in stages them to the deterministic `AiVA/Engage/{jobNo}` folder.
 */
export async function fetchDriveFolderChildrenByPath(
  token: string,
  drivePath: string,
  folderPath: string,
  refresh?: RefreshTokenFn,
): Promise<GraphDriveItem[]> {
  const encoded = folderPath.split("/").map(encodeURIComponent).join("/");
  try {
    const result = await graphFetch<GraphList<GraphDriveItem>>(
      `${drivePath}/root:/${encoded}:/children?$top=200&$orderby=name&$select=id,name,size,webUrl,lastModifiedDateTime,file,folder`,
      token,
      refresh,
    );
    return result.value.map((it) => ({
      id: it.id,
      name: it.name,
      size: it.size,
      webUrl: it.webUrl,
      lastModifiedDateTime: it.lastModifiedDateTime,
      file: it.file,
      folder: it.folder,
    }));
  } catch (e) {
    if (e instanceof GraphApiError && e.status === 404) return [];
    throw e;
  }
}

/** One driveItem's identity, needed to look up its tags in a batch. */
interface TagLookup {
  id: string;
  driveId: string | null;
}

/**
 * Fetch tags for many items in as few round-trips as possible via the Graph
 * `$batch` endpoint (≤20 subrequests each) — instead of one call per item, which
 * floods the network on every search. Each subrequest reads an item's listItem
 * fields; 404s (e.g. OneDrive items with no listItem) are skipped. Returns a map
 * of item id → tag values (only for items that have any). Best-effort: a failed
 * batch simply contributes no tags.
 */
async function fetchTagsBatch(
  token: string,
  items: TagLookup[],
  refresh?: RefreshTokenFn,
): Promise<Map<string, string[]>> {
  const map = new Map<string, string[]>();
  const targets = items.filter((it): it is { id: string; driveId: string } => it.driveId != null);
  const BATCH_SIZE = 20;

  for (let start = 0; start < targets.length; start += BATCH_SIZE) {
    const entries = await fetchChunkTags(targets.slice(start, start + BATCH_SIZE), token, refresh); // NOSONAR chunks are fetched one at a time to respect Graph throttling
    for (const [id, tags] of entries) map.set(id, tags);
  }
  return map;
}

/** One `$batch` subresponse carrying a driveItem's listItem fields. */
interface BatchResponse {
  responses?: Array<{ id: string; status: number; body?: { fields?: Record<string, unknown> } }>;
}

/**
 * Fetch tags for a single ≤20-item chunk via one `$batch` call, returning
 * `[itemId, tags]` pairs (only for items that have any). Best-effort: a network
 * throw or non-2xx yields no pairs. Split out of {@link fetchTagsBatch} so each
 * function's branching stays modest.
 */
async function fetchChunkTags(
  chunk: Array<{ id: string; driveId: string }>,
  token: string,
  refresh?: RefreshTokenFn,
): Promise<Array<[string, string[]]>> {
  const body = JSON.stringify({
    requests: chunk.map((it, idx) => ({
      id: String(idx),
      method: "GET",
      url: `/drives/${it.driveId}/items/${it.id}/listItem?$expand=fields`,
    })),
  });

  let response: Response;
  try {
    response = await graphPost(`${GRAPH_BASE}/$batch`, body, token, refresh);
  } catch {
    return [];
  }
  if (!response.ok) return [];

  const data = (await response.json()) as BatchResponse;
  const entries: Array<[string, string[]]> = [];
  for (const r of data.responses ?? []) {
    if (r.status !== 200) continue;
    const item = chunk[Number(r.id)];
    if (!item) continue;
    const tags = extractTags(r.body?.fields);
    if (tags.length) entries.push([item.id, tags]);
  }
  return entries;
}

export async function fetchSharePointSites(token: string, refresh?: RefreshTokenFn): Promise<GraphSite[]> {
  const result = await graphFetch<GraphList<GraphSite>>(
    "/sites?search=*&$select=id,name,displayName,description,webUrl,lastModifiedDateTime",
    token,
    refresh,
  );
  return result.value;
}

/**
 * Request a short-lived, read-only embed preview URL for a drive item via
 * `POST {drivePath}/items/{itemId}/preview`, returning its `getUrl` (or null if
 * Graph omits it).
 *
 * This is how the Knowledge Base keeps every file type view-only (AIVA-7093).
 * Opening the item's own `webUrl` is not reliably read-only: SharePoint routes
 * Word/Excel to a reading view but drops PowerPoint into its editor and PDFs
 * into an annotatable viewer ("Pen"/"Save changes") for anyone with write
 * permission — even with `?action=view`/`embedview`. The preview surface renders
 * Word, Excel, PowerPoint, PDF, and text uniformly with no editing tools. The
 * url is embeddable and expires after a few minutes, which is fine for an
 * immediate open in a new tab. Requires only the `Files.Read` scope.
 */
export async function fetchItemPreviewUrl(
  token: string,
  drivePath: string,
  itemId: string,
  refresh?: RefreshTokenFn,
): Promise<string | null> {
  const url = `${GRAPH_BASE}${drivePath}/items/${itemId}/preview`;
  const response = await graphPost(url, "{}", token, refresh);
  if (!response.ok) {
    throw new GraphApiError(response.status, `Graph preview request failed: ${response.status}`);
  }
  const data = (await response.json()) as { getUrl?: string };
  return data.getUrl ?? null;
}

/**
 * Create a DURABLE, read-only (view) sharing link for a drive item, scoped to
 * the organisation, and return its web URL — or null if none is returned.
 *
 * Unlike the item's raw `webUrl` (which downloads the file — and gets caught by
 * MCAS as a "Blocked_…" download), a view sharing link opens the file in the
 * browser's read-only viewer, the same view-only experience the Knowledge Base
 * gives. Unlike `fetchItemPreviewUrl` (whose embed URL expires in minutes), this
 * link is persistent, so it's safe to hand to Engage and open later (AIVA-7093).
 * `drivePath` is e.g. `/me/drive`. Requires the `Files.ReadWrite` scope.
 */
export async function createReadOnlyLink(
  token: string,
  drivePath: string,
  itemId: string,
  refresh?: RefreshTokenFn,
): Promise<string | null> {
  const url = `${GRAPH_BASE}${drivePath}/items/${itemId}/createLink`;
  const body = JSON.stringify({ type: "view", scope: "organization" });
  const response = await graphPost(url, body, token, refresh);
  if (!response.ok) {
    throw new GraphApiError(response.status, `Graph createLink request failed: ${response.status}`);
  }
  const data = (await response.json()) as { link?: { webUrl?: string } };
  return data.link?.webUrl ?? null;
}

/** Parent reference on a search hit's driveItem resource. */
interface SearchParentReference {
  driveId?: string;
  siteId?: string;
  name?: string;
  path?: string;
}

/** A driveItem-shaped resource inside a Microsoft Search hit. */
interface SearchHitResource {
  id?: string;
  name?: string;
  webUrl?: string;
  size?: number;
  lastModifiedDateTime?: string;
  file?: { mimeType: string };
  folder?: { childCount: number };
  parentReference?: SearchParentReference;
  listItem?: { fields?: Record<string, unknown> };
}

interface SearchQueryResponse {
  value?: Array<{ hitsContainers?: Array<{ hits?: Array<{ resource?: SearchHitResource }> }> }>;
}

/**
 * Derive a short "which library / folder" label from a hit's parentReference.
 * Prefers the drive/library name, then the tail of the folder path (the part
 * after `root:`), then a generic fallback.
 */
function deriveSourceLabel(ref: SearchParentReference | undefined): string {
  if (ref?.name) return ref.name;
  const path = ref?.path ?? "";
  const afterRoot = path.split("root:")[1];
  if (afterRoot) {
    const segments = afterRoot.split("/").filter(Boolean);
    if (segments.length > 0) return segments.slice(-2).join("/");
  }
  return "SharePoint";
}

/**
 * Build a KQL query string for the Knowledge Base search. Each whitespace term
 * is matched (prefix, trailing `*`) and ANDed together.
 *
 * By default we match ONLY the file `filename` / `title` — so typing "aiva"
 * returns files/folders NAMED "aiva…", not every document that merely mentions
 * it in its body or lives in an aiva-named library. That free-text/content match
 * was the reason a name search surfaced unrelated files. Each term matches by
 * prefix wildcard (`term*`) OR exact phrase (`"term"`) — both — since a quoted
 * phrase can't carry a trailing wildcard in KQL.
 *
 * `includeContent` re-adds an EXACT-word free-text clause (`"SOP"`, no wildcard).
 * It's used for the search-bar / tag-chip search, where the term is a TAG value
 * (e.g. "SOP") that SharePoint indexes as free text (Enterprise Keywords / column
 * values) rather than in the filename — so a tag lookup needs the content-index
 * match. Exact (not prefix) so a tag search matches the whole tag and doesn't
 * pull partial tag words or body-content prefixes. Filename/title still match by
 * prefix wildcard in this mode so filename type-ahead keeps working.
 *
 * Only trailing (prefix) wildcards are used because KQL supports prefix matching
 * only — leading/infix wildcards (`*term`, `*term*`) are NOT supported by
 * Microsoft Search. Matching is case-insensitive (the index normalises case).
 * Alphanumerics plus `-`/`_` are allowed; other KQL operator characters — and a
 * leading `-`/`+` (the NOT/AND operators) — are stripped from user input.
 */
function buildSearchQuery(raw: string, includeContent = false): string {
  const terms = raw
    .split(/\s+/)
    // Allow alphanumerics plus '-' and '_' (both valid and common in filenames).
    // Strip KQL structural/operator characters (" * ? ( ) : | &) and any LEADING
    // '-'/'+', which KQL interprets as the NOT/AND operators (so "-foo" would
    // EXCLUDE "foo" instead of searching for it). Internal '-'/'_' are kept —
    // the index tokenizes them the same way inside filenames.
    .map((t) => t.replace(/["*?():|&]/g, "").replace(/^[-+]+/, "").trim())
    .filter(Boolean);
  if (terms.length === 0) return raw;
  return terms
    .map((t) =>
      includeContent
        // Tag / search-bar path: filename & title still match by PREFIX wildcard
        // (`SOP*`) so filename type-ahead works, but the free-text/tag clause is
        // EXACT word only (`"SOP"`) — no bare `SOP*` — so a tag search matches the
        // whole tag exactly and doesn't pull partial tag words or body-content
        // prefixes (e.g. "SOP" won't match "SOPs" or "soprano").
        ? `(filename:${t}* OR title:${t}* OR "${t}")`
        // Name/title path: prefix wildcard (`term*`) OR exact phrase
        // (`"term"`) — both — scoped to filename/title so body content is
        // never matched. Quoting can't carry a wildcard, so the two forms are
        // ORed as separate clauses.
        : `(filename:${t}* OR title:${t}* OR filename:"${t}" OR title:"${t}")`,
    )
    .join(" ");
}

/**
 * Whether a search hit is a folder. The Microsoft Search index does NOT return
 * the driveItem `folder` facet — it stamps folders with a generic file facet
 * (`application/octet-stream`, size 0) and omits `folder` — so a facet check
 * alone misses them. We therefore also treat a name with no file extension as a
 * folder (files in a document library carry an extension: .docx, .pdf, .eml…),
 * which is what actually distinguishes "AiVA Test" (folder) from "Test.md".
 */
function isFolderHit(r: SearchHitResource): boolean {
  if (r.folder != null) return true;
  const name = r.name ?? "";
  return !/\.[a-z0-9]{1,8}$/i.test(name);
}

/**
 * Tenant-wide document search via the Microsoft Search API
 * (`POST /search/query`, entityTypes: driveItem). Matches on the file
 * NAME/TITLE only (see buildNameQuery) — NOT body content — across every
 * SharePoint site the signed-in user can access. Returns file hits only
 * (folders/sites dropped), deduped, normalised to GraphSearchItem. Requires the
 * Files.Read / Sites.Read.All delegated scopes the add-in already uses. Uses its
 * own POST (graphFetch is GET-only), mirroring fetchItemPreviewUrl.
 */
export async function searchDriveItems(
  token: string,
  queryText: string,
  refresh?: RefreshTokenFn,
  opts: { size?: number; from?: number; includeContent?: boolean } = {},
): Promise<GraphSearchItem[]> {
  const body = JSON.stringify({
    requests: [
      {
        entityTypes: ["driveItem"],
        query: { queryString: buildSearchQuery(queryText, opts.includeContent) },
        from: opts.from ?? 0,
        size: opts.size ?? 25,
        // NOTE: do NOT set `fields` here — specifying it makes Search return ONLY
        // those properties, stripping id/name/file/parentReference from every hit
        // (which then all fail the filter below → zero results). Tags are added
        // afterwards via a per-item listItem fetch (enrichWithTags).
      },
    ],
  });

  const response = await graphPost(`${GRAPH_BASE}/search/query`, body, token, refresh);
  if (!response.ok) {
    throw new GraphApiError(response.status, `Graph search request failed: ${response.status}`);
  }

  const data = (await response.json()) as SearchQueryResponse;
  const hits = data.value?.[0]?.hitsContainers?.[0]?.hits ?? [];
  const items = hits
    .map((h) => h.resource)
    .filter((r): r is SearchHitResource => r?.id != null && (r.file != null || r.folder != null))
    .map((r) => ({
      id: r.id!,
      name: r.name ?? "Untitled",
      webUrl: r.webUrl ?? "",
      size: r.size ?? 0,
      lastModifiedDateTime: r.lastModifiedDateTime ?? "",
      driveId: r.parentReference?.driveId ?? null,
      sourceLabel: deriveSourceLabel(r.parentReference),
      isFolder: isFolderHit(r),
    }));

  // The same document often surfaces as several hits - the same file copied to
  // more than one location (e.g. a team site AND the user's OneDrive) comes back
  // with a distinct id/webUrl/driveId per copy, rendering as visible duplicates.
  // Collapse on a content-identity proxy (name + size + last-modified) so identical
  // copies show once, while genuinely different files that merely share a name
  // (different size or date) are both kept. First hit wins (rank order).
  const seen = new Set<string>();
  const deduped: GraphSearchItem[] = [];
  for (const item of items) {
    const key = `${item.name.toLowerCase()} ${item.size} ${item.lastModifiedDateTime}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(item);
  }

  // Search hits don't carry column values, so fetch each result's tags from its
  // listItem fields (parallel, best-effort). This is why `fields` isn't requested
  // on the search itself — doing so would strip the hit's core properties.
  // One batched round-trip (per 20 results) to attach tags, instead of a
  // per-result call storm.
  const tagsById = await fetchTagsBatch(token, deduped, refresh);
  return deduped.map((item) => {
    const tags = tagsById.get(item.id);
    return tags ? { ...item, tags } : item;
  });
}

/**
 * Collect the distinct tag values in use across the tenant, for the Knowledge
 * Base tag-filter chips (which must render on every page, including the site
 * list where the items themselves carry no tags). Runs one broad tenant-wide
 * Microsoft Search (`queryString: "*"`, driveItems) to sample tagged documents,
 * then reads their tags via the same batched listItem lookup as search. Sampled,
 * not exhaustive — capped at `sampleSize` documents so opening the module costs
 * one search plus a bounded number of `$batch` calls. Returns a sorted, distinct
 * tag list; best-effort (an empty list on any failure).
 */
export async function fetchTenantTags(
  token: string,
  refresh?: RefreshTokenFn,
  // 100 docs = at most 5 `$batch` round-trips (20 sub-requests each). The tag
  // vocabulary is small and repeats heavily across documents, so a sample this
  // size captures the common tags; the union with each page's own item tags
  // fills in anything rarer while browsing. Result is session-cached by the hook.
  sampleSize = 100,
): Promise<string[]> {
  const body = JSON.stringify({
    requests: [
      {
        entityTypes: ["driveItem"],
        // "*" matches everything — we only need a representative sample of
        // documents to harvest the tag vocabulary, not a ranked result set.
        query: { queryString: "*" },
        from: 0,
        size: sampleSize,
      },
    ],
  });

  const response = await graphPost(`${GRAPH_BASE}/search/query`, body, token, refresh);
  if (!response.ok) {
    throw new GraphApiError(response.status, `Graph tag search failed: ${response.status}`);
  }

  const data = (await response.json()) as SearchQueryResponse;
  const hits = data.value?.[0]?.hitsContainers?.[0]?.hits ?? [];
  const lookups: TagLookup[] = hits
    .map((h) => h.resource)
    .filter((r): r is SearchHitResource => r?.id != null)
    .map((r) => ({ id: r.id!, driveId: r.parentReference?.driveId ?? null }));

  const tagsById = await fetchTagsBatch(token, lookups, refresh);
  const all = new Set<string>();
  for (const tags of tagsById.values()) {
    for (const tag of tags) all.add(tag);
  }
  return [...all].sort((a, b) => a.localeCompare(b));
}

/** Decode a base64 string to raw bytes for a binary upload body. */
function base64ToBytes(base64: string): Uint8Array {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.codePointAt(i) ?? 0;
  return bytes;
}

/**
 * PUT a body to the signed-in user's OneDrive at `{folderPath}/{fileName}`
 * (folders auto-created), returning the drive item. Shared by the base64-bytes
 * and raw-text uploaders. Requires the `Files.ReadWrite` Graph scope.
 */
async function putToOneDrive(
  token: string,
  folderPath: string,
  fileName: string,
  body: BodyInit,
  contentType: string,
  refresh?: RefreshTokenFn,
): Promise<GraphDriveItem> {
  const encodedPath = `${folderPath}/${fileName}`
    .split("/")
    .map((seg) => encodeURIComponent(seg))
    .join("/");
  const url = `${GRAPH_BASE}/me/drive/root:/${encodedPath}:/content`;
  const response = await graphSend(
    (bearer) =>
      fetch(url, {
        method: "PUT",
        headers: { Authorization: `Bearer ${bearer}`, "Content-Type": contentType },
        body,
      }),
    "PUT",
    sanitisePath(url),
    token,
    refresh,
  );

  if (!response.ok) {
    throw new GraphApiError(response.status, `OneDrive upload failed: ${response.status}`);
  }
  return response.json() as Promise<GraphDriveItem>;
}

/**
 * Upload a base64 file to the signed-in user's OneDrive at
 * `{folderPath}/{fileName}` (folders auto-created), returning the drive item.
 * Used to stage Engage attachments for FILEURL-mode sites, which read the file
 * from OneDrive rather than from an inline SaveAttachments blob (AIVA-6989).
 */
export function uploadFileToOneDrive(
  token: string,
  folderPath: string,
  fileName: string,
  base64Content: string,
  contentType: string,
  refresh?: RefreshTokenFn,
): Promise<GraphDriveItem> {
  const body = new Blob([base64ToBytes(base64Content)], { type: contentType });
  return putToOneDrive(token, folderPath, fileName, body, contentType, refresh);
}

/**
 * Upload raw content (e.g. an email's `.eml` MIME bytes) to OneDrive at
 * `{folderPath}/{fileName}`. Mirrors the prototype's `graphUploadFileRaw`, used
 * to save the exported email into the job's folder (AIVA-6990). `content` is a
 * Blob so the message bytes are preserved exactly (never re-encoded as text).
 */
export function uploadRawToOneDrive(
  token: string,
  folderPath: string,
  fileName: string,
  content: Blob,
  contentType: string,
  refresh?: RefreshTokenFn,
): Promise<GraphDriveItem> {
  return putToOneDrive(token, folderPath, fileName, content, contentType, refresh);
}

/**
 * Fetch the raw RFC822 MIME of a message via `GET /me/messages/{restId}/$value`
 * (mirrors the prototype's `graphFetchRaw`). `restId` must be a Graph REST id.
 * Returns the `.eml` content as a Blob — the raw response bytes, NOT decoded as
 * text, so binary message parts aren't corrupted/bloated (AIVA-6990).
 */
export async function fetchEmailMime(
  token: string,
  restId: string,
  refresh?: RefreshTokenFn,
): Promise<Blob> {
  const url = `${GRAPH_BASE}/me/messages/${restId}/$value`;
  const response = await graphSend(
    (bearer) => fetch(url, { headers: { Authorization: `Bearer ${bearer}` } }),
    "GET",
    sanitisePath(url),
    token,
    refresh,
  );

  if (!response.ok) {
    throw new GraphApiError(response.status, `Graph MIME fetch failed: ${response.status}`);
  }
  return response.blob();
}
